import "./MentionedVideosList.css";
import { Button } from "@ui/Button.tsx";
import { Card } from "@ui/Card.tsx";
import { formatDurationClockShort } from "@ui/format.ts";
import { usePendingImage } from "@ui/hooks/usePendingImage";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { HStack, VStack } from "@ui/Stack";
import { Text } from "@ui/typography/Text.tsx";
import { TextMono } from "@ui/typography/TextMono.tsx";
import { pluralise } from "@utils/text";
import { createSignal, createUniqueId, For, onMount, Show } from "solid-js";
import {
	installMentionClipboard,
	videoMentionUrl,
} from "../utils/mentionClipboard";
import {
	mentionClipDurationSec,
	type PlayVideoMentionsHandler,
	playVideoMentions,
} from "../utils/videoMentionPlayback.ts";
import {
	addVideoMentionToClipboard,
	isVideoMentionInClipboard,
} from "./clipboard/clipboardStore.ts";
import type { MentionedVideoItem } from "./VideoMention.tsx";
import { VideoMentionMarker } from "./VideoMentionMarker.tsx";

type MentionedVideosListProps = {
	videoMentions: MentionedVideoItem[];
	appEmbedded: boolean;
	offersClipboard?: boolean;
	onPlayVideoMentions?: PlayVideoMentionsHandler;
};

function scrollElementIntoView(target: HTMLElement) {
	target.scrollIntoView({
		behavior: "smooth",
		block: "nearest",
		inline: "nearest",
	});
}

function scrollToVideoMention(id: string) {
	const selector = `[data-video-mention-id="${CSS.escape(id)}"]`;
	const marker = document.querySelector(
		`.video-mention${selector}`,
	) as HTMLElement | null;
	// Prefer the inline citation marker, but a mention-only paragraph is
	// collapsed (display:none) during hydration, leaving its marker with no
	// layout box. In that case fall back to the mentioned-video thumbnail
	// (the element containing the clip image) for the same mention.
	if (marker !== null && marker.getClientRects().length > 0) {
		scrollElementIntoView(marker);
		return;
	}

	const thumbnail = document.querySelector(
		`.mentioned-video-thumbnail${selector}`,
	) as HTMLElement | null;
	if (thumbnail !== null) {
		scrollElementIntoView(thumbnail);
		return;
	}

	if (marker !== null) {
		scrollElementIntoView(marker);
	}
}

export const MentionedVideosList = (props: MentionedVideosListProps) => {
	const [expanded, setExpanded] = createSignal(false);
	const listId = createUniqueId();

	// Copying a source writes the link below in the QuestionBox's own form, so
	// that the clip survives being pasted into another app (see mentionClipboard).
	onMount(installMentionClipboard);

	const playVideoMention = (videoMention: MentionedVideoItem) => {
		playVideoMentions(props.videoMentions, videoMention.id, {
			appEmbedded: props.appEmbedded,
			onPlayVideoMentions: props.onPlayVideoMentions,
		});
	};

	return (
		<Card class="mentioned-videos-list" theme="white">
			<VStack spacing="none">
				<Button
					class="mentioned-videos-toggle"
					classList={{ open: expanded() }}
					variant="ghost"
					theme="monochrome"
					aria-expanded={expanded()}
					aria-controls={listId}
					onClick={() => setExpanded(!expanded())}
				>
					<HStack spacing="medium" align="center">
						<TextMono weight="bold" transform="uppercase" color="fg2">
							Sources · {props.videoMentions.length}{" "}
							{pluralise(props.videoMentions.length, "clip")}
						</TextMono>
						<MaterialSymbol
							symbol="keyboard_arrow_down"
							class="expand"
							relativeSize="even-larger"
							color="fg4"
						/>
					</HStack>
				</Button>
				<div
					id={listId}
					class="mentioned-videos-container accordion"
					classList={{ open: expanded() }}
				>
					<div class="accordion-contents">
						<VStack class="mentioned-videos-rows" spacing="medium">
							<For each={props.videoMentions}>
								{(videoMention) => {
									const thumbnail = usePendingImage(
										() => videoMention.thumbnail_url,
									);
									return (
										<div class="mentioned-video">
											<HStack
												fullWidth
												spacing="medium"
												align="center"
												justify="space-between"
											>
												<HStack spacing="small" align="center" grow>
													{/* Citation jump and play are different actions, so
												    they cannot share one button. */}
													<HStack spacing="medium" align="center" grow>
														<button
															type="button"
															class="mentioned-video__index"
															aria-label={`Scroll to video ${videoMention.index} mention`}
															onClick={() => {
																scrollToVideoMention(videoMention.id);
															}}
														>
															{/* No scissors here: the heading already counts these
														    as clips, so a clip is what every row stands for. */}
															<VideoMentionMarker index={videoMention.index} />
														</button>
														<button
															type="button"
															class="mentioned-video__main"
															aria-label={`Play video ${videoMention.index}`}
															onClick={() => playVideoMention(videoMention)}
														>
															<img
																class="mentioned-video-thumbnail"
																src={thumbnail.src()}
																alt=""
																// An image has no content to hold a link, so the clip's
																// `/mention` link is carried here for the copy handler.
																data-mention-href={videoMentionUrl(
																	videoMention,
																)}
															/>
															<span class="mentioned-video-info">
																<Show
																	when={videoMention.title}
																	fallback={
																		<Text size="small" color="fg1">
																			Video {videoMention.index}
																		</Text>
																	}
																>
																	<Text
																		size="small"
																		color="fg1"
																		ellipsis="tooltip"
																	>
																		{videoMention.title}
																	</Text>
																</Show>
																<Text size="small" color="fg1">
																	·
																</Text>
																<Text size="small" color="fg1">
																	{formatDurationClockShort(
																		mentionClipDurationSec(videoMention),
																	)}
																</Text>
															</span>
														</button>
														<Button
															symbol="subdirectory_arrow_left"
															symbolColor="fg4"
															symbolRelativeSize="larger"
															variant="ghost"
															theme="monochrome"
															size="small"
															aria-label={`Scroll to video ${videoMention.index} mention`}
															onClick={() => {
																scrollToVideoMention(videoMention.id);
															}}
														/>
													</HStack>
												</HStack>
												<Show when={props.offersClipboard}>
													<Button
														class="mentioned-video-add-button"
														symbol={
															isVideoMentionInClipboard(videoMention)
																? "check"
																: "add"
														}
														symbolColor={
															isVideoMentionInClipboard(videoMention)
																? "purple"
																: "fg4"
														}
														symbolRelativeSize="larger"
														variant="secondary"
														theme="monochrome"
														size="small"
														aria-label={`Add video ${videoMention.index}`}
														onClick={() => {
															addVideoMentionToClipboard(videoMention);
														}}
													/>
												</Show>
											</HStack>
										</div>
									);
								}}
							</For>
						</VStack>
					</div>
				</div>
			</VStack>
		</Card>
	);
};
